import { useEffect, useState } from "react";
import { Users, Gift, XCircle } from "@phosphor-icons/react";
import { toast } from "../components/ui/sonner";
import { getSubscribers, setCompAccess } from "../lib/api";

const STATUS_STYLES = {
  active: "bg-green-500/10 text-green-400 border-green-500/30",
  trialing: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  comp: "bg-purple-500/10 text-purple-400 border-purple-500/30",
  past_due: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
  canceled: "bg-red-500/10 text-red-400 border-red-500/30",
  inactive: "bg-zinc-800 text-zinc-400 border-zinc-700",
};

const StatusBadge = ({ status }) => (
  <span
    data-testid={`subscriber-status-${status}`}
    className={`rounded-full border px-2 py-0.5 font-mono text-xs uppercase ${STATUS_STYLES[status] || STATUS_STYLES.inactive}`}
  >
    {status}
  </span>
);

const SubscriberAdmin = () => {
  const [subscribers, setSubscribers] = useState(null);
  const [error, setError] = useState(false);
  const [pendingId, setPendingId] = useState(null);

  useEffect(() => {
    getSubscribers()
      .then(setSubscribers)
      .catch(() => setError(true));
  }, []);

  const handleToggleComp = async (sub) => {
    const grant = sub.subscription_status !== "comp";
    setPendingId(sub.id);
    try {
      await setCompAccess(sub.id, grant);
      setSubscribers((prev) => prev.map((s) => (s.id === sub.id ? { ...s, subscription_status: grant ? "comp" : "inactive" } : s)));
      toast.success(grant ? `Free access granted to ${sub.email}` : `Free access revoked for ${sub.email}`);
    } catch {
      toast.error("Could not update access");
    } finally {
      setPendingId(null);
    }
  };

  const counts = (subscribers || []).reduce((acc, s) => {
    acc[s.subscription_status] = (acc[s.subscription_status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div data-testid="subscriber-admin-page" className="mx-auto max-w-[1200px] px-4 py-8 sm:px-6">
      <div className="mb-6 flex items-center gap-2">
        <Users size={24} weight="bold" className="text-eb-accent" />
        <h1 className="font-heading text-2xl uppercase tracking-tight text-white sm:text-3xl">Subscribers</h1>
      </div>

      <div className="mb-6 flex gap-3">
        {Object.entries(counts).map(([status, count]) => (
          <div key={status} data-testid={`subscriber-count-${status}`} className="rounded-md border border-white/10 bg-eb-surface px-4 py-2">
            <div className="font-heading text-2xl text-white">{count}</div>
            <div className="text-xs uppercase text-zinc-500">{status}</div>
          </div>
        ))}
      </div>

      {error && (
        <div data-testid="subscriber-admin-error" className="rounded-md border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
          Could not load subscribers.
        </div>
      )}

      {!error && !subscribers && <p className="text-sm text-zinc-500">Loading&hellip;</p>}

      {!error && subscribers && subscribers.length === 0 && (
        <div data-testid="subscriber-admin-empty" className="rounded-lg border border-dashed border-zinc-800 px-6 py-16 text-center text-zinc-500">
          No subscribers yet.
        </div>
      )}

      {!error && subscribers && subscribers.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-white/10">
          <table data-testid="subscriber-admin-table" className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-white/10 bg-eb-surface text-xs uppercase text-zinc-500">
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Stripe Customer</th>
                <th className="px-4 py-3">Joined</th>
                <th className="px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {subscribers.map((s) => (
                <tr key={s.id} data-testid={`subscriber-row-${s.id}`} className="border-b border-white/5 text-zinc-300">
                  <td className="px-4 py-3">{s.email}</td>
                  <td className="px-4 py-3">{s.name || "\u2014"}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={s.subscription_status} />
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-500">{s.stripe_customer_id || "\u2014"}</td>
                  <td className="px-4 py-3 text-zinc-500">{s.created_at ? new Date(s.created_at).toLocaleDateString() : "\u2014"}</td>
                  <td className="px-4 py-3">
                    <button
                      data-testid={`subscriber-comp-toggle-${s.id}`}
                      onClick={() => handleToggleComp(s)}
                      disabled={pendingId === s.id}
                      className={`flex items-center gap-1.5 rounded-md border px-2 py-1 font-mono text-xs uppercase transition-colors disabled:opacity-50 ${
                        s.subscription_status === "comp"
                          ? "border-red-500/30 text-red-400 hover:bg-red-500/10"
                          : "border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                      }`}
                    >
                      {s.subscription_status === "comp" ? (
                        <>
                          <XCircle size={14} weight="bold" /> Revoke
                        </>
                      ) : (
                        <>
                          <Gift size={14} weight="bold" /> Grant Free Access
                        </>
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SubscriberAdmin;
